import { Component, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useAnimations, useGLTF } from "@react-three/drei";
import { SWIMMERS_3D, SPAWN_CONFIG_3D } from "../config/swimmers3dConfig.js";
import createDriftBehavior from "../behaviors/drift.js";
import createCrossBehavior from "../behaviors/cross.js";
import "../styles/FishSwimmer3D.css";

const BEHAVIORS = { drift: createDriftBehavior, cross: createCrossBehavior };
const randomBetween = ({ min, max }) => min + Math.random() * (max - min);

const cloneScene = (source) => {
  const clone = source.clone(true);
  clone.traverse((object) => {
    if (!object.isMesh) return;
    object.geometry = object.geometry.clone();
    if (Array.isArray(object.material)) {
      object.material = object.material.map((material) => material.clone());
    } else {
      object.material = object.material.clone();
    }
  });
  return clone;
};

const disposeScene = (scene) => {
  scene.traverse((object) => {
    if (!object.isMesh) return;
    object.geometry.dispose();
    if (Array.isArray(object.material)) {
      object.material.forEach((material) => material.dispose());
    } else {
      object.material.dispose();
    }
  });
};

function CameraSetup({ fov }) {
  const { camera, size } = useThree();

  useEffect(() => {
    camera.fov = fov;
    camera.aspect = size.width / size.height;
    camera.position.z = (size.height / 2) / Math.tan((fov * Math.PI) / 360);
    camera.updateProjectionMatrix();
  }, [camera, fov, size.width, size.height]);

  return null;
}

function FishInstance({ entry, viewport, onFinished }) {
  const group = useRef();
  const elapsed = useRef(0);
  const gltf = useGLTF(entry.model);
  const scene = useMemo(() => cloneScene(gltf.scene), [gltf.scene]);
  const behavior = useMemo(() => {
    const factory = BEHAVIORS[entry.behavior];
    return factory ? factory(entry) : createDriftBehavior(entry);
  }, [entry]);
  const { actions, mixer } = useAnimations(gltf.animations, group);
  const scale = entry.size / 2;

  useEffect(() => {
    const action = Object.values(actions)[0];
    if (!action) return undefined;
    action.reset().play();
    action.setEffectiveTimeScale(entry.animationSpeed ?? 1);
    mixer.setTime(Math.random() * (action.getClip().duration || 0));
    return () => mixer.stopAllAction();
  }, [actions, entry.animationSpeed, mixer]);

  useEffect(() => () => disposeScene(scene), [scene]);

  useFrame((_state, delta) => {
    elapsed.current += delta;
    const motion = behavior.update(elapsed.current, delta, viewport);
    group.current.position.set(motion.position.x, motion.position.y, motion.position.z);
    group.current.rotation.set(motion.rotation.x, motion.rotation.y, motion.rotation.z);
    const introScale = entry.persistent ? Math.min(1, elapsed.current / 0.8) : 1;
    group.current.scale.setScalar(scale * introScale);
    if (motion.finished) onFinished();
  });

  return (
    <group ref={group}>
      <primitive object={scene} />
    </group>
  );
}

function Scene({ active, onFinished }) {
  const { size } = useThree();
  const viewport = useMemo(
    () => ({ width: size.width, height: size.height }),
    [size.width, size.height],
  );

  return (
    <>
      <CameraSetup fov={35} />
      <hemisphereLight args={[0xffffff, 0xb8d4e8, 1.9]} />
      <directionalLight position={[-2, 3, 4]} intensity={1.6} />
      {active.map((entry) => (
        <FishInstance
          key={entry.key}
          entry={entry.config}
          viewport={viewport}
          onFinished={() => onFinished(entry.key)}
        />
      ))}
    </>
  );
}

class FishErrorBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function supportsWebGL() {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  return reduced;
}

export default function FishSwimmer3D() {
  const reducedMotion = useReducedMotion();
  const [webglAvailable, setWebglAvailable] = useState(false);
  const [active, setActive] = useState(() =>
    SWIMMERS_3D
      .filter((config) => config.persistent)
      .map((config) => ({ key: `${config.type}-persistent`, config })),
  );
  const activeRef = useRef(active);

  useEffect(() => setWebglAvailable(supportsWebGL()), []);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    if (reducedMotion) return undefined;
    let checkTimer;
    let cancelled = false;
    const spawned = () => activeRef.current.filter((entry) => !entry.config.persistent);

    const spawnCheck = () => {
      if (cancelled) return;
      const current = spawned();
      const available = SWIMMERS_3D.filter(
        (config) =>
          !config.persistent &&
          !current.some((entry) => entry.config.type === config.type),
      );
      const chance = Math.pow(SPAWN_CONFIG_3D.decay, current.length);

      if (available.length > 0 && Math.random() < chance) {
        const config = available[Math.floor(Math.random() * available.length)];
        const key = `${config.type}-${Date.now()}`;
        setActive((previous) => [...previous, { key, config }]);
      }

      checkTimer = setTimeout(spawnCheck, randomBetween(SPAWN_CONFIG_3D.checkDelay));
    };

    checkTimer = setTimeout(spawnCheck, SPAWN_CONFIG_3D.firstCheckDelay);
    return () => {
      cancelled = true;
      clearTimeout(checkTimer);
    };
  }, [reducedMotion]);

  const handleFinished = (key) => {
    setActive((previous) => previous.filter((entry) => entry.key !== key));
  };

  if (reducedMotion || !webglAvailable || active.length === 0) return null;

  return (
    <FishErrorBoundary>
      <div className="fish-swimmer-3d" aria-hidden="true">
        <Canvas
          flat
          dpr={[1, 2]}
          gl={{ antialias: true, alpha: true }}
          camera={{ fov: 35, near: 0.1, far: 5000, position: [0, 0, 1000] }}
        >
          <Suspense fallback={null}>
            <Scene
              active={active}
              onFinished={handleFinished}
            />
          </Suspense>
        </Canvas>
      </div>
    </FishErrorBoundary>
  );
}

SWIMMERS_3D.forEach(({ model }) => useGLTF.preload(model));
